import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Wallet, Calendar, Building2, Clock, Ban, Info } from "lucide-react";
import { formatCurrency, formatDate, brTodayStr } from "@/lib/api";

// Days between two YYYY-MM-DD strings in BR calendar (matches backend)
function daysBetween(fromStr, toStr) {
  const [fy, fm, fd] = fromStr.split("-").map(Number);
  const [ty, tm, td] = toStr.split("-").map(Number);
  const a = Date.UTC(fy, fm - 1, fd);
  const b = Date.UTC(ty, tm - 1, td);
  return Math.round((b - a) / 86400000);
}

// Stable colour per company so the same employer always keeps the same chip tint
const COMPANY_PALETTE = [
  { ring: "ring-blue-500/40", bg: "bg-blue-500/15", text: "text-blue-300", border: "border-blue-500/30", dot: "bg-blue-400" },
  { ring: "ring-emerald-500/40", bg: "bg-emerald-500/15", text: "text-emerald-300", border: "border-emerald-500/30", dot: "bg-emerald-400" },
  { ring: "ring-amber-500/40", bg: "bg-amber-500/15", text: "text-amber-300", border: "border-amber-500/30", dot: "bg-amber-400" },
  { ring: "ring-fuchsia-500/40", bg: "bg-fuchsia-500/15", text: "text-fuchsia-300", border: "border-fuchsia-500/30", dot: "bg-fuchsia-400" },
  { ring: "ring-cyan-500/40", bg: "bg-cyan-500/15", text: "text-cyan-300", border: "border-cyan-500/30", dot: "bg-cyan-400" },
  { ring: "ring-rose-500/40", bg: "bg-rose-500/15", text: "text-rose-300", border: "border-rose-500/30", dot: "bg-rose-400" },
];
function companyStyle(id) {
  if (!id) return COMPANY_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  return COMPANY_PALETTE[Math.abs(hash) % COMPANY_PALETTE.length];
}

export default function EmployeeReceivables({ receivables, selectedIds, toggleSelect, toggleAll, anticipationPct, simulation, simulationError, getRecLiquid, deductions, quoteLoading, isRowQuoted }) {
  const [detailsRec, setDetailsRec] = useState(null);
  const ded = deductions || {};
  const uniqueCompanies = useMemo(() => {
    const set = new Set(receivables.map((r) => r.company_id).filter(Boolean));
    return set.size;
  }, [receivables]);
  const eligibleReceivables = useMemo(() => receivables.filter((r) => !r.in_grace_period && !r.anticipation_blocked), [receivables]);
  const graceCount = receivables.filter((r) => r.in_grace_period).length;
  const cutoffCount = receivables.filter((r) => r.anticipation_blocked && !r.in_grace_period).length;
  // Líquido a receber: SOMENTE os elegíveis (não bloqueados / fora de carência), para não inflar e dar
  // impressão errada de deságio enorme quando metade está com prazo expirado.
  const valueAtMaturityEligible = useMemo(
    () => eligibleReceivables.reduce((s, r) => s + (r.net_receivable || r.net_value || r.gross_value || 0), 0),
    [eligibleReceivables],
  );
  // Valor autorizado p/ antecipação = pct × líquido elegível (só mostra se < 100)
  const showAuthorized = anticipationPct < 100 && hasEligible(eligibleReceivables);
  const authorizedValue = valueAtMaturityEligible * (anticipationPct / 100);
  const hasEligibleFlag = eligibleReceivables.length > 0;

  return (
    <div className="py-6 space-y-5 stagger-children" data-testid="employee-receivables">
      {/* Top card — Opção C limpa: Líquido a receber → [Valor autorizado] → Se antecipar agora */}
      <Card data-testid="employee-balance-card" className="border-emerald-500/20 bg-gradient-to-br from-emerald-500/[0.06] to-transparent animate-fade-in shadow-[0_0_0_1px_rgba(16,185,129,0.18),0_8px_30px_-10px_rgba(16,185,129,0.25)]">
        <CardContent className="p-5">
          {/* Linha 1: Líquido a receber no vencimento (apenas elegíveis) */}
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Líquido a receber no vencimento</p>
            <p data-testid="balance-value-at-maturity" className="text-base font-medium text-foreground/90 tabular-nums shrink-0">
              {formatCurrency(valueAtMaturityEligible)}
            </p>
          </div>

          {/* Linha 2: Valor autorizado p/ antecipação (só quando pct < 100) */}
          {showAuthorized && (
            <div data-testid="balance-authorized-row" className="flex items-baseline justify-between gap-3 mt-2">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-blue-300/80">
                Valor autorizado p/ antecipação
                <span className="ml-1 text-[9px] font-medium text-blue-300/60 normal-case tracking-normal">({anticipationPct}%)</span>
              </p>
              <p data-testid="balance-authorized-value" className="text-base font-medium text-blue-300 tabular-nums shrink-0">
                {formatCurrency(authorizedValue)}
              </p>
            </div>
          )}

          {/* Divisor */}
          <div className="my-4 border-t border-dashed border-white/10" />

          {/* Linha 3: Se antecipar agora — destaque grande */}
          {simulation ? (
            <>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-300 mb-1.5">Se antecipar agora, você recebe</p>
              <p data-testid="balance-value-anticipation" className="text-3xl font-bold text-emerald-300 tracking-tight tabular-nums">
                {formatCurrency(simulation.total_liquido)}
              </p>
            </>
          ) : hasEligibleFlag ? (
            <div data-testid="balance-simulation-pending">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-400/80 mb-1">Se antecipar agora</p>
              <p className="text-base text-amber-300/90 font-medium">Simulação indisponível no momento</p>
              <p className="text-[11px] text-amber-300/60 mt-0.5">
                {simulationError?.message ? "Tente novamente em instantes" : "Aguarde alguns instantes"}
              </p>
            </div>
          ) : (
            <div data-testid="balance-no-eligible">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Se antecipar agora</p>
              <p className="text-base text-muted-foreground font-medium">Indisponível</p>
              <p className="text-[11px] text-muted-foreground/70 mt-0.5">Sem recebíveis elegíveis</p>
            </div>
          )}

          {/* Footer: badges */}
          <div className="flex items-center gap-2 mt-4 pt-3 border-t border-white/5 flex-wrap">
            <span className="text-[11px] text-muted-foreground">
              {eligibleReceivables.length} recebível{eligibleReceivables.length !== 1 ? "is" : ""} disponível{eligibleReceivables.length !== 1 ? "is" : ""}
            </span>
            {graceCount > 0 && (
              <span data-testid="grace-period-summary" className="inline-flex items-center gap-1 text-[9px] font-medium text-amber-300 bg-amber-500/10 border border-amber-500/30 px-2 py-0.5 rounded-full">
                <Clock className="w-3 h-3" />
                {graceCount} em carência
              </span>
            )}
            {cutoffCount > 0 && (
              <span data-testid="cutoff-summary" className="inline-flex items-center gap-1 text-[9px] font-medium text-red-300 bg-red-500/10 border border-red-500/30 px-2 py-0.5 rounded-full">
                <Ban className="w-3 h-3" />
                {cutoffCount} sem prazo p/ antecipação
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Select All */}
      {eligibleReceivables.length > 0 && (
        <div className="flex items-center gap-3 animate-fade-in">
          <div
            role="button" tabIndex={0}
            onClick={toggleAll} data-testid="select-all-checkbox"
            className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold h-9 px-4 cursor-pointer transition-colors ${
              selectedIds.size === eligibleReceivables.length && eligibleReceivables.length > 0
                ? "bg-blue-600 hover:bg-blue-700 text-white"
                : "border border-blue-500/30 text-blue-400 hover:bg-blue-500/10 hover:text-blue-300"
            }`}>
            <Checkbox checked={selectedIds.size === eligibleReceivables.length && eligibleReceivables.length > 0}
              className="border-current data-[state=checked]:bg-white data-[state=checked]:text-blue-600 pointer-events-none" />
            Selecionar todos ({eligibleReceivables.length})
          </div>
        </div>
      )}

      {/* Receivables List */}
      {receivables.length === 0 ? (
        <div className="text-center py-12 animate-fade-in">
          <Wallet className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Nenhum recebível disponível</p>
        </div>
      ) : (
        <div className="space-y-3">
          {receivables.map((r) => {
            const antecipavel_liquido = getRecLiquid(r);
            const recNet = r.net_receivable ?? r.gross_value ?? 0;
            const recAuthorized = recNet * (anticipationPct / 100);
            const hasDetails = (r.deductions || 0) > 0 || (r.acrescimos || 0) > 0 || (r.despesas_acessorias || 0) > 0;
            const companyTint = uniqueCompanies > 1 ? companyStyle(r.company_id) : null;
            const inGrace = !!r.in_grace_period;
            const isBlocked = !!r.anticipation_blocked;
            const isDisabled = inGrace || isBlocked;
            // `net_basis === "display"` (item 14): a linha ficou FORA do
            // conjunto cotado, então o líquido dela é informativo — vem de uma
            // passada de exibição e não é somável com a base cotada.
            //
            // INVARIANTE (conferida em routes/receivables.py, montagem de
            // `quotable`): `display` ⟺ `in_grace_period || anticipation_blocked`
            // — o conjunto cotável é exatamente o complemento. Logo uma linha
            // `display` é sempre `isDisabled` aqui, e o rótulo "(informativo)"
            // só precisa existir no ramo desabilitado. Se um dia o backend
            // passar a marcar `display` numa linha selecionável, o rótulo some
            // e é este comentário que aponta onde religá-lo.
            const isDisplayOnly = r.net_basis === "display";
            const isSelected = selectedIds.has(r.id);
            // Frescor POR LINHA: só com entrada no `receivables_map` da cotação
            // corrente o valor é firme. Sem ela (erro, mapa parcial, cotação de
            // outra seleção/versão da lista) o número é estimativa local.
            const rowQuoted = isSelected && !!isRowQuoted?.(r);
            const rowApprox = isSelected && !rowQuoted;
            const graceDaysLeft = r.grace_days_left || 0;
            const daysUntilDue = inGrace && r.due_date ? daysBetween(brTodayStr(), r.due_date) : null;
            const maturesBeforeGraceEnds = inGrace && daysUntilDue !== null && daysUntilDue <= graceDaysLeft;
            const graceLabel = maturesBeforeGraceEnds
              ? "Antecipação ainda não aprovada"
              : `Disponível em ${graceDaysLeft} dia${graceDaysLeft !== 1 ? "s" : ""}`;
            return (
            <Card key={r.id} data-testid={`receivable-item-${r.id}`}
              data-grace={inGrace ? "true" : "false"}
              onClick={() => { if (!isDisabled) toggleSelect(r.id); }}
              className={`card-interactive animate-fade-in overflow-hidden ${
                isDisabled
                  ? "border-amber-500/20 opacity-60 cursor-not-allowed"
                  : selectedIds.has(r.id) ? "border-blue-500/40 bg-blue-500/5 cursor-pointer" : "border-white/5 cursor-pointer"
              }`}>
              {companyTint && r.company_name && (
                <div
                  data-testid={`receivable-company-${r.id}`}
                  className={`flex items-center gap-2 px-4 py-1.5 ${companyTint.bg} border-b ${companyTint.border}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${companyTint.dot} shrink-0`} aria-hidden />
                  <Building2 className={`w-3 h-3 ${companyTint.text} shrink-0`} />
                  <span className={`text-[11px] font-semibold ${companyTint.text} truncate`}>
                    {r.company_name}
                  </span>
                </div>
              )}
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <Checkbox checked={isSelected} disabled={isDisabled} data-testid={`receivable-checkbox-${r.id}`}
                    className="border-white/20 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 shrink-0 disabled:opacity-40" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{r.description}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Calendar className="w-3 h-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">{formatDate(r.due_date)}</span>
                      {inGrace && (
                        <span data-testid={`receivable-grace-badge-${r.id}`}
                          data-grace-mode={maturesBeforeGraceEnds ? "matured" : "countdown"}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold bg-amber-500/15 border border-amber-500/30 text-amber-300">
                          <Clock className="w-3 h-3" />
                          {graceLabel}
                        </span>
                      )}
                      {isBlocked && !inGrace && (
                        <span data-testid={`receivable-cutoff-badge-${r.id}`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold bg-red-500/15 border border-red-500/30 text-red-300">
                          <Ban className="w-3 h-3" />
                          Sem prazo p/ antecipação
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    {isDisabled ? (
                      <>
                        <p className="text-[10px] uppercase tracking-wide mb-0.5 text-muted-foreground"
                          data-testid={`receivable-net-label-${r.id}`}
                          data-net-basis={r.net_basis || ""}>
                          {isDisplayOnly ? "A receber (informativo)" : "A receber"}
                        </p>
                        <p className="text-base font-semibold tabular-nums text-muted-foreground" data-testid={`receivable-gross-net-${r.id}`}>{formatCurrency(recNet)}</p>
                      </>
                    ) : (
                      <>
                        {/* Ramo selecionável: `net_basis` é sempre "quoted"
                            aqui (ver invariante acima) — sem rótulo especial. */}
                        <p className="text-[10px] uppercase tracking-wide mb-0.5 text-muted-foreground"
                          data-testid={`receivable-net-label-${r.id}`}
                          data-net-basis={r.net_basis || ""}>
                          A receber
                        </p>
                        <p className="text-[11px] tabular-nums text-foreground/70" data-testid={`receivable-gross-net-${r.id}`}>{formatCurrency(recNet)}</p>
                        {anticipationPct < 100 && (
                          <>
                            <p className="text-[10px] uppercase tracking-wide mt-1.5 mb-0.5 text-blue-300/80 font-semibold">Autorizado p/ antecipação</p>
                            <p className="text-xs font-medium text-blue-300 tabular-nums" data-testid={`receivable-authorized-${r.id}`}>{formatCurrency(recAuthorized)}</p>
                          </>
                        )}
                        {antecipavel_liquido !== recNet && (
                          <>
                            <p className="text-[10px] uppercase tracking-wide mt-1.5 mb-0.5 text-emerald-400 font-semibold">Antecipando agora</p>
                            {/* Linha SELECIONADA: valor firme só quando ESTA
                                linha veio na cotação corrente; senão "≈"
                                (estimativa local) e pulse enquanto recota. */}
                            <p
                              data-testid={`receivable-net-${r.id}`}
                              data-quote-state={!isSelected ? undefined : rowQuoted ? "fresh" : quoteLoading ? "loading" : "approx"}
                              aria-live={isSelected ? "polite" : undefined}
                              className={`text-lg font-bold text-emerald-400 tabular-nums tracking-tight ${rowApprox && quoteLoading ? "animate-pulse" : ""}`}>
                              {rowApprox ? "≈ " : ""}{formatCurrency(antecipavel_liquido)}
                              {rowApprox && !quoteLoading && (
                                <span className="sr-only"> — valor aproximado</span>
                              )}
                            </p>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
                {hasDetails && (
                  <div className="mt-3 pt-3 border-t border-white/5 flex justify-end">
                    <button
                      type="button"
                      data-testid={`receivable-details-btn-${r.id}`}
                      onClick={(e) => { e.stopPropagation(); setDetailsRec(r); }}
                      className="inline-flex items-center gap-1.5 text-[11px] font-medium text-blue-400 hover:text-blue-300 transition-colors">
                      <Info className="w-3 h-3" />
                      Ver detalhes
                    </button>
                  </div>
                )}
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}

      {/* Dialog: Detalhes do recebível (Bruto / Acréscimos / Descontos / Despesas não descontadas) */}
      <Dialog open={!!detailsRec} onOpenChange={(open) => !open && setDetailsRec(null)}>
        <DialogContent className="sm:max-w-md border-white/10" data-testid="receivable-details-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg">Detalhes do recebível</DialogTitle>
            <DialogDescription>
              {detailsRec?.description}{detailsRec?.due_date ? ` · ${formatDate(detailsRec.due_date)}` : ""}
            </DialogDescription>
          </DialogHeader>
          {detailsRec && (
            <div className="space-y-2.5 py-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground/90">Valor bruto</span>
                <span className="font-medium tabular-nums" data-testid="details-gross">{formatCurrency(detailsRec.gross_value)}</span>
              </div>
              {(detailsRec.acrescimos || 0) > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-emerald-400">Acréscimos</span>
                  <span className="font-medium tabular-nums text-emerald-400" data-testid="details-acrescimos">+ {formatCurrency(detailsRec.acrescimos)}</span>
                </div>
              )}
              {(detailsRec.deductions || 0) > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-red-400">Descontos</span>
                  <span className="font-medium tabular-nums text-red-400" data-testid="details-descontos">− {formatCurrency(detailsRec.deductions)}</span>
                </div>
              )}
              <div className="border-t border-white/10 my-2" />
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-emerald-300">Líquido a receber no vencimento</span>
                <span className="text-base font-bold tabular-nums text-emerald-300" data-testid="details-liquido">{formatCurrency(detailsRec.net_receivable ?? detailsRec.gross_value)}</span>
              </div>
              {detailsRec.net_basis === "display" && (
                <p className="text-[11px] text-muted-foreground/80" data-testid="details-net-basis-display">
                  Valor informativo: este recebível está fora da antecipação agora, então o líquido
                  acima é uma estimativa de exibição — não é o valor cotado.
                </p>
              )}
              {(detailsRec.despesas_acessorias || 0) > 0 && (
                <div className="mt-4 pt-3 border-t border-dashed border-white/10">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm text-purple-300 font-medium">Despesas não descontadas de você</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">Custos da empresa que não afetam seu líquido</p>
                    </div>
                    <span className="font-medium tabular-nums text-purple-300 text-sm shrink-0" data-testid="details-despesas-nao-descontadas">{formatCurrency(detailsRec.despesas_acessorias)}</span>
                  </div>
                </div>
              )}
            </div>
          )}
          <Button
            data-testid="receivable-details-close-btn"
            onClick={() => setDetailsRec(null)}
            variant="outline"
            className="w-full border-white/10">
            Fechar
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// helper kept outside so it doesn't recompute on every render
function hasEligible(list) { return Array.isArray(list) && list.length > 0; }
